import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Button, Loading } from '../components/ui';
import { api } from '../services/api';
import { formatPrice } from '../utils/helpers';
import { PaymentMethodSelect, PayMethod, CRYPTO_MIN_RUB } from '../components/PaymentMethodSelect';
import { CODE_SERVICES } from './CodesPage';
import { CodeFaq, PurchaseTerms } from '../components/CodeServiceInfo';
import { CODE_FAQ } from '../utils/codeFaq';
import WebApp from '@twa-dev/sdk';

interface Denomination {
  id: string;
  name: string;
  priceRub: number;
  countryCode?: string;
  group?: string;
}

// Регион активации кода. У поставщика один сервис (например Apple) собран из
// номиналов трех десятков стран — показывать их одним списком нельзя,
// поэтому страна выбирается отдельно, а номиналы фильтруются по ней.
const COUNTRY_NAMES: Record<string, string> = {
  TR: 'Турция', US: 'США', RU: 'Россия', UA: 'Украина', BY: 'Беларусь', KZ: 'Казахстан',
  UK: 'Великобритания', EU: 'Европа', GLOB: 'Global', DE: 'Германия', FR: 'Франция',
  IT: 'Италия', ES: 'Испания', PT: 'Португалия', NL: 'Нидерланды', BE: 'Бельгия',
  AT: 'Австрия', CH: 'Швейцария', IE: 'Ирландия', PL: 'Польша', FI: 'Финляндия',
  SE: 'Швеция', NO: 'Норвегия', DK: 'Дания', LU: 'Люксембург', CZ: 'Чехия',
  SK: 'Словакия', HU: 'Венгрия', HR: 'Хорватия', RO: 'Румыния', CA: 'Канада',
  MX: 'Мексика', BR: 'Бразилия', AR: 'Аргентина', AU: 'Австралия', NZ: 'Новая Зеландия',
  JP: 'Япония', CN: 'Китай', HK: 'Гонконг', SG: 'Сингапур', MY: 'Малайзия',
  ID: 'Индонезия', TH: 'Таиланд', PH: 'Филиппины', IN: 'Индия', AE: 'ОАЭ',
  SA: 'Саудовская Аравия', QA: 'Катар', KW: 'Кувейт', BH: 'Бахрейн', OM: 'Оман',
  ZA: 'ЮАР',
};

// Самые ходовые регионы — наверх списка, остальные по алфавиту.
const COUNTRY_TOP = ['TR', 'US', 'RU'];

const countryOf = (d: Denomination): string => d.countryCode || 'GLOB';

const countryFlag = (code: string): string => {
  if (code === 'GLOB') return '🌍';
  const cc = code === 'UK' ? 'GB' : code;
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...Array.from(cc).map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
};

const countryLabel = (code: string): string => {
  const name = COUNTRY_NAMES[code] || code;
  const flag = countryFlag(code);
  return flag ? `${flag} ${name}` : name;
};

const compareCountries = (a: string, b: string): number => {
  const ia = COUNTRY_TOP.indexOf(a);
  const ib = COUNTRY_TOP.indexOf(b);
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  return (COUNTRY_NAMES[a] || a).localeCompare(COUNTRY_NAMES[b] || b, 'ru');
};

interface PickerOption {
  value: string;
  label: string;
  /** Правая колонка строки — например цена номинала */
  trailing?: string;
}

/**
 * Строка-карточка со своим выпадающим списком. Нативный select на iOS открывает
 * системный пикер, выбивающийся из оформления, поэтому список свой: панель под
 * строкой, плавное раскрытие и прокрутка к выбранному пункту.
 */
const PickerRow: React.FC<{
  title: string;
  subtitle?: string;
  valueLabel: string;
  valueBold?: boolean;
  groups: Array<[string, PickerOption[]]>;
  value: string | null;
  onSelect: (v: string) => void;
}> = ({ title, subtitle, valueLabel, valueBold, groups, value, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false); // отдельный флаг: нужен кадр между монтированием и анимацией
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = useCallback(() => {
    setShown(false);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  }, []);

  const toggle = () => {
    if (open) { close(); return; }
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
  };

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open, close]);

  // Открыли длинный список — сразу показываем выбранный пункт
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: 'center' });
  }, [open]);

  const showGroupTitles = groups.length > 1;

  const handlePick = (v: string) => {
    try {
      (window as any).Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
    } catch { /* ignore */ }
    onSelect(v);
    close();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={toggle}
        className="w-full bg-white p-4 flex items-center justify-between text-left"
        style={{ borderRadius: '16px', border: '1px solid #E1E3E5' }}
      >
        <div className="min-w-0 pr-3">
          <h2 className="text-[15px] font-bold text-text-primary">{title}</h2>
          {subtitle && <p className="text-[12.5px] text-text-secondary mt-0.5 truncate">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span
            className={valueBold ? 'text-[15px] font-bold' : 'text-[14px] font-semibold'}
            style={valueBold ? { fontFamily: 'Manrope, sans-serif', color: '#1A1D1A' } : { color: '#1A1D1A' }}
          >
            {valueLabel}
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8B8E97"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: shown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-30 bg-white overflow-hidden"
          style={{
            top: 'calc(100% + 6px)',
            borderRadius: '16px',
            border: '1px solid #E1E3E5',
            boxShadow: '0 12px 32px rgba(10,31,68,0.14)',
            opacity: shown ? 1 : 0,
            transform: shown ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.98)',
            transformOrigin: 'top center',
            transition: 'opacity 160ms ease, transform 160ms cubic-bezier(0.16, 1, 0.3, 1)',
            pointerEvents: shown ? 'auto' : 'none',
          }}
        >
          <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: '300px' }}>
            {groups.map(([group, list]) => (
              <div key={group}>
                {showGroupTitles && (
                  <div
                    className="px-4 pt-3 pb-1 sticky top-0 bg-white"
                    style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: '#8B8E97' }}
                  >
                    {group.toUpperCase()}
                  </div>
                )}
                {list.map((opt) => {
                  const active = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      data-active={active}
                      onClick={() => handlePick(opt.value)}
                      className="w-full flex items-center gap-2 px-4 py-3 text-left"
                      style={{ background: active ? 'rgba(54,87,240,0.06)' : 'transparent' }}
                    >
                      <span
                        className="flex-1 min-w-0 truncate text-[14px]"
                        style={{ color: active ? '#3657F0' : '#1A1D1A', fontWeight: active ? 600 : 400 }}
                      >
                        {opt.label}
                      </span>
                      {opt.trailing && (
                        <span
                          className="text-[14px] font-bold flex-shrink-0"
                          style={{ fontFamily: 'Manrope, sans-serif', color: active ? '#3657F0' : '#1A1D1A' }}
                        >
                          {opt.trailing}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Страница сервиса кодов пополнения (AppRoute): выбор номинала, количество, оплата.
export const CodeServicePage: React.FC = () => {
  const { serviceKey } = useParams<{ serviceKey: string }>();
  const navigate = useNavigate();

  const meta = CODE_SERVICES.find((s) => s.key === serviceKey);

  const [isLoading, setIsLoading] = useState(true);
  const [title, setTitle] = useState(meta?.name || '');
  const [denominations, setDenominations] = useState<Denomination[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [country, setCountry] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PayMethod>('SBP');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getCodeServices()
      .then((r) => {
        if (cancelled) return;
        const svc = r.services.find((s) => s.key === serviceKey);
        if (svc) {
          setTitle(svc.title);
          setDenominations(svc.denominations);
          if (svc.denominations.length > 0) {
            const defaultCountry = Array.from(new Set(svc.denominations.map(countryOf))).sort(compareCountries)[0];
            setCountry(defaultCountry);
            const first = svc.denominations.find((d) => countryOf(d) === defaultCountry);
            setSelectedId(first ? first.id : svc.denominations[0].id);
          }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [serviceKey]);

  const countries = useMemo(
    () => Array.from(new Set(denominations.map(countryOf))).sort(compareCountries),
    [denominations]
  );
  const visible = useMemo(
    () => denominations.filter((d) => countryOf(d) === country),
    [denominations, country]
  );
  // Номиналы страны, сгруппированные по линейке — станут optgroup в списке
  const groups = useMemo(() => {
    const map = new Map<string, Denomination[]>();
    visible.forEach((d) => {
      const g = d.group || title || 'Номиналы';
      const list = map.get(g);
      if (list) list.push(d);
      else map.set(g, [d]);
    });
    return Array.from(map.entries());
  }, [visible, title]);
  const selected = useMemo(
    () => denominations.find((d) => d.id === selectedId) || null,
    [denominations, selectedId]
  );

  // Смена страны: номиналы другие, поэтому выбор и количество сбрасываем
  const handleCountry = (code: string) => {
    setCountry(code);
    const first = denominations.find((d) => countryOf(d) === code);
    setSelectedId(first ? first.id : null);
    setQuantity(1);
  };
  const total = selected ? selected.priceRub * quantity : 0;

  // Крипта недоступна ниже минималки Cardlink (~$10)
  const cryptoDisabled = total < CRYPTO_MIN_RUB;
  useEffect(() => {
    if (cryptoDisabled && paymentMethod === 'CRYPTO') setPaymentMethod('SBP');
  }, [cryptoDisabled, paymentMethod]);

  const handleBuy = async () => {
    if (!selected || isProcessing) return;
    setIsProcessing(true);
    try {
      const order = await api.createCodeOrder(selected.id, quantity, paymentMethod);
      // Как в корзине: бот пришлет ссылку на оплату в чат — mini app закрывается
      if ((order as any).useBotFlow) {
        try {
          (window as any).Telegram?.WebApp?.close?.();
        } catch { /* ignore */ }
        return;
      }
      navigate(`/payment/${order.id}`, {
        state: {
          paymentUrl: (order as any).paymentUrl || '',
          stubPayAvailable: (order as any).stubPayAvailable || false,
        },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Не удалось создать заказ');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Layout showNav={false}>
      <div className="flex flex-col min-h-full pb-28" style={{ background: '#F5F7FA' }}>
        {/* Шапка сервиса */}
        <div className="px-4 pt-4 pb-3 flex items-center gap-3">
          {meta && (
            <img
              src={meta.logo}
              alt=""
              style={{ width: 44, height: 44, borderRadius: 14 }}
            />
          )}
          <div>
            <h1 className="text-[20px] font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1D1A' }}>
              {title || 'Коды пополнения'}
            </h1>
            <p className="text-[13px] text-text-secondary">Код придет сразу после оплаты</p>
          </div>
        </div>

        {isLoading ? (
          <div className="pt-16"><Loading /></div>
        ) : denominations.length === 0 ? (
          <div className="px-4 pt-8 text-center">
            <p className="text-[15px] font-semibold text-text-primary mb-1">Пока нет в наличии</p>
            <p className="text-[13px] text-text-secondary mb-4">Загляните позже — позиции скоро появятся</p>
            <Button variant="secondary" size="sm" onClick={() => navigate('/codes')}>К сервисам</Button>
          </div>
        ) : (
          <div className="px-4 flex flex-col gap-3">
            {/* Страна активации */}
            {countries.length > 1 && (
              <PickerRow
                title="Страна"
                subtitle="Регион, где активируется код"
                valueLabel={countryLabel(country)}
                groups={[['Страны', countries.map((c) => ({ value: c, label: countryLabel(c) }))]]}
                value={country}
                onSelect={handleCountry}
              />
            )}

            {/* Номинал — сгруппирован по товарным линейкам поставщика
                (у Valorant это Riot Cash Card и Valorant Gift Card) */}
            <PickerRow
              title="Номинал"
              subtitle={selected ? selected.name : 'Выберите номинал'}
              valueLabel={selected ? formatPrice(selected.priceRub) : '—'}
              valueBold
              groups={groups.map(([group, list]) => [
                group,
                list.map((d) => ({ value: d.id, label: d.name, trailing: formatPrice(d.priceRub) })),
              ])}
              value={selectedId}
              onSelect={setSelectedId}
            />

            {/* Количество */}
            <div className="bg-white p-4 flex items-center justify-between" style={{ borderRadius: '16px', border: '1px solid #E1E3E5' }}>
              <h2 className="text-[15px] font-bold text-text-primary">Количество</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-lg font-bold active:scale-[0.95] transition-transform"
                  style={{ background: '#F4F4F7', color: quantity > 1 ? '#1A1D1A' : '#B0B3BA' }}
                >
                  −
                </button>
                <span className="text-[17px] font-bold w-6 text-center" style={{ color: '#1A1D1A' }}>{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => Math.min(10, q + 1))}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-lg font-bold active:scale-[0.95] transition-transform"
                  style={{ background: '#F4F4F7', color: quantity < 10 ? '#1A1D1A' : '#B0B3BA' }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Способ оплаты */}
            <div className="bg-white p-4" style={{ borderRadius: '16px', border: '1px solid #E1E3E5' }}>
              <PaymentMethodSelect value={paymentMethod} onChange={setPaymentMethod} cryptoDisabled={cryptoDisabled} />
            </div>

            {/* Итого */}
            {selected && (
              <div className="flex items-center flex-wrap gap-2.5 px-1">
                <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '28px', lineHeight: '120%', color: '#1A1D1A' }}>
                  {formatPrice(total)}
                </span>
              </div>
            )}

            <PurchaseTerms buttonLabel="Оплатить" />
          </div>
        )}

        {serviceKey && CODE_FAQ[serviceKey] && (
          <div className="px-4 mt-6">
            <CodeFaq items={CODE_FAQ[serviceKey]} />
          </div>
        )}

        {/* Fixed CTA */}
        {denominations.length > 0 && (
          <div
            className="fixed left-0 right-0 z-30 bg-white"
            style={{
              bottom: '0',
              paddingBottom: Math.max(
                (WebApp as any).safeAreaInset?.bottom || 0,
                (WebApp as any).contentSafeAreaInset?.bottom || 0,
                8
              ),
            }}
          >
            <div style={{ padding: '8px 16px 0 16px' }}>
              <Button
                onClick={handleBuy}
                disabled={!selected || isProcessing}
                fullWidth
                size="md"
                style={{ height: '52px', fontSize: '16px', fontWeight: 600, borderRadius: '16px' }}
              >
                {isProcessing ? 'Создаем заказ…' : `Оплатить ${total > 0 ? formatPrice(total) : ''}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
